import React, { useCallback } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Box, Button, FormControlLabel, Switch } from "@mui/material";
import { Dividend } from "@rentablo/api/apiv1";
import {
  MobileListHeader,
  MobileListSettingsButton,
  MonthGroupedList,
  SettingsFieldGroup,
  SettingsSections,
} from "@components/MobileList";
import { MissingDividendsNotice } from "../MissingDividendsNotice";
import { DividendMobileRow } from "./DividendMobileRow";

const dividendDate = (dividend: Dividend) =>
  new Date(dividend.date).toISOString();
const dividendId = (dividend: Dividend) => dividend.bookingId ?? dividend.date;

interface Props {
  dividends: Dividend[];
  investmentId: string;
  unreceivedCount: number;
  splitAdjust: boolean;
  onSplitAdjustChange: (splitAdjust: boolean) => void;
  isRefreshing: boolean;
}

export const DividendsMobileList = ({
  dividends,
  investmentId,
  unreceivedCount,
  splitAdjust,
  onSplitAdjustChange,
  isRefreshing,
}: Props) => {
  const { t } = useTranslation();

  const renderDividend = useCallback(
    (dividend: Dividend) => (
      <DividendMobileRow dividend={dividend} investmentId={investmentId} />
    ),
    [investmentId]
  );

  return (
    <Box
      data-testid="dividends-mobile-list"
      aria-busy={isRefreshing}
      sx={{ opacity: isRefreshing ? 0.6 : 1 }}
    >
      <Box mb={2}>
        <MissingDividendsNotice
          count={unreceivedCount}
          investmentId={investmentId}
        />
      </Box>
      <MobileListHeader
        count={t("investment.dividends.mobile.count", {
          count: dividends.length,
        })}
      >
        <Button
          variant="outlined"
          size="small"
          component={Link}
          to={`/investments/${investmentId}/dividend-wizard/create`}
        >
          {t("investment.dividends.addDividends")}
        </Button>
        <MobileListSettingsButton
          hasChanges={!splitAdjust}
          canReset={!splitAdjust}
          onReset={() => onSplitAdjustChange(true)}
        >
          <SettingsSections testId="dividends-settings">
            <SettingsFieldGroup>
              <FormControlLabel
                control={
                  <Switch
                    size="medium"
                    checked={splitAdjust}
                    onChange={(_, checked) => onSplitAdjustChange(checked)}
                  />
                }
                label={t("investment.dividends.splitAdjust")}
                sx={{ m: 0 }}
              />
            </SettingsFieldGroup>
          </SettingsSections>
        </MobileListSettingsButton>
      </MobileListHeader>

      <MonthGroupedList
        items={dividends}
        getDate={dividendDate}
        getId={dividendId}
        renderItem={renderDividend}
        period="year"
        emptyText={t("domains.dividends.dividendsTable.noDividends")}
        withLeading={false}
      />
    </Box>
  );
};
